import { AfterViewChecked, Component, ElementRef, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TextFieldModule } from '@angular/cdk/text-field';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material/material.module';
import { HELP_CHAT_MAX_CHARS, HelpChatMessage, HelpChatService } from './help-chat.service';
import { renderHelpAnswer } from './help-chat-text';

// questions to start from, so people see what they can ask
const SUGGESTIONS = [
    "How do I create a model?",
    "What's the difference between current state and a project?",
    "How does LINQ work out costs?",
    "How can I find where time is spent?",
]

// the help chat: answers questions about using LINQ. A panel beside the page rather than over it, so the user can
// follow its advice while it's open
@Component({
    selector: 'app-help-chat',
    templateUrl: './help-chat.html',
    styleUrls: ['./help-chat.scss'],
    standalone: true,
    imports: [MaterialModule, ReactiveFormsModule, TextFieldModule]
})
export class HelpChatComponent implements AfterViewChecked {
    private static dialogRef:MatDialogRef<HelpChatComponent>|undefined

    suggestions = SUGGESTIONS
    HELP_CHAT_MAX_CHARS = HELP_CHAT_MAX_CHARS
    question = new FormControl('', {nonNullable:true})

    // answers are rendered once each, rather than on every check
    private rendered = new WeakMap<HelpChatMessage, SafeHtml>()
    private shown = -1

    @ViewChild('scroller') scroller:ElementRef<HTMLElement>

    constructor(public chat:HelpChatService, private sanitizer:DomSanitizer) {}

    // opens the panel, or brings the one already open into focus
    static open(dialog:MatDialog) {
        if (HelpChatComponent.dialogRef) return HelpChatComponent.dialogRef
        const ref = dialog.open(HelpChatComponent, {
            hasBackdrop: false,
            position: {right: '24px', bottom: '24px'},
            width: '420px',
            maxWidth: 'calc(100vw - 32px)',
            height: '620px',
            maxHeight: 'calc(100vh - 96px)',
            panelClass: 'help-chat-panel',
            autoFocus: 'textarea',
            // it stays beside whatever the user moves to, such as a guide it links to
            closeOnNavigation: false,
        })
        HelpChatComponent.dialogRef = ref
        ref.afterClosed().subscribe(() => HelpChatComponent.dialogRef = undefined)
        return ref
    }

    // the answer as HTML. The renderer escapes everything before adding its few tags, so it is trusted as it is
    answerHtml(message:HelpChatMessage):SafeHtml {
        let html = this.rendered.get(message)
        if (!html) {
            html = this.sanitizer.bypassSecurityTrustHtml(renderHelpAnswer(message.content))
            this.rendered.set(message, html)
        }
        return html
    }

    send() {
        const question = this.question.value
        if (!question.trim() || this.chat.asking) return
        this.question.setValue('')
        this.chat.ask(question)
    }

    // Enter sends; Shift+Enter starts a new line
    onKeydown(event:KeyboardEvent) {
        if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
            event.preventDefault()
            this.send()
        }
    }

    ask(suggestion:string) {
        this.chat.ask(suggestion)
    }

    // the latest message, or the wait for one, is kept in view
    ngAfterViewChecked() {
        const count = this.chat.messages.length + (this.chat.asking ? 1 : 0) + (this.chat.error ? 1 : 0)
        if (count === this.shown || !this.scroller) return
        this.shown = count
        const el = this.scroller.nativeElement
        el.scrollTop = el.scrollHeight
    }
}
